// ════════════════════════════════════════════════════════════════════════
//  Schuladmin einladen
//
//  Läuft über eine Edge Function, nicht über den Client: ein Konto anzulegen
//  geht nur mit dem Service-Key, und der darf nie in einen Browser.
//
//  Es ist die Function invite-school-admin und nicht invite-teacher: die hängt
//  jedes Profil an die Schule des Aufrufers, und ein Plattformkonto hat keine.
// ════════════════════════════════════════════════════════════════════════

import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { OttoClient } from "@otto/plattform";
import { schoolKeys } from "../lib/data.js";

/**
 * Der Fehler einer Einladung: Code als `message`, HTTP-Status daneben.
 *
 * Der Status reist mit, weil ein unbekannter Code sonst als „fehlgeschlagen“ auf
 * dem Bildschirm steht und niemand mehr sagen kann, woran es lag. Genau das ist
 * beim Postfachlimit passiert.
 */
export type InviteFailure = Error & { readonly status?: number };

export function useInviteSchoolAdmin(client: OttoClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { schoolId: string; email: string; name: string }) => {
      const { data, error } = await client.functions.invoke<{
        ok?: boolean;
        error?: string;
      }>("invite-school-admin", {
        body: { school_id: input.schoolId, email: input.email, name: input.name },
      });

      if (error) {
        // Der Code steht im Antwortkörper. Ohne diesen Umweg zeigt die
        // Oberfläche „FunctionsHttpError“, und das hilft niemandem.
        let code = "unbekannt";
        const response = (error as { context?: Response }).context;
        if (response) {
          const parsed = (await response.json().catch(() => ({}))) as { error?: string };
          code = parsed.error ?? "unbekannt";
        }
        // Ohne `response` gar keine Eigenschaft statt einer mit `undefined`:
        // `exactOptionalPropertyTypes` unterscheidet das, und zu Recht – „nicht
        // bekannt“ und „bekannt als undefiniert“ sind zwei Aussagen.
        const failure: InviteFailure = Object.assign(
          new Error(code),
          response ? { status: response.status } : {},
        );
        throw failure;
      }
      if (data?.error) throw new Error(data.error);
      return data;
    },
    // Zwei Listen zeigen das Ergebnis: die Zahl der Schuladmins in der
    // Schulliste und der Name in den Konten dieser Schule. Die zweite fehlte
    // hier – die Einladung war verschickt, das Konto angelegt, und die Liste
    // darüber zeigte weiter den alten Stand. Aufgefallen ist es dem E2E-Test,
    // der nach dem Einladen den Namen sucht.
    onSuccess: (_data, input) => {
      void queryClient.invalidateQueries({ queryKey: schoolKeys.schools });
      void queryClient.invalidateQueries({ queryKey: schoolKeys.people(input.schoolId) });
    },
  });
}
